import {Injectable, inject} from '@angular/core';
import {AuthService} from '@auth/data-access';
import {
	BackendService,
	ICreateStampRequest,
	ICreateStampResponse,
	ICreateTempUserResponse,
	ICreateUserGroupRequest,
	ICreateUserGroupResponse,
	ICreateUserRequest_,
	ICreateUserResponse_,
	IDeleteGroupRequest,
	IDeleteStampRequest,
	IDeleteUserRequest,
	IDeleteUserResponse,
	IGetGroupRequestParams_,
	IGetGroupResponse_,
	IGetGroupsResponse_,
	IGetPortraitRequest_,
	IGetPortraitResponse_,
	IGetUserRequest,
	IGetUserResponse_,
	IGetUserStampsRequest,
	IGetUserStampsResponse,
	IListUsersResponse,
	IQueryUsersRequestParams,
	IQueryUsersResponse,
	IResetTempUsersLanguageResponse,
	IUpdateGroupRequest_,
	IUpdateGroupResponse_,
	IUpdatePortraitRequest,
	IUpdateUserRequest_,
	IUpdateUserResponse,
	IUpdateUserResponse_,
} from '@core/api/data-access';
import {Observable} from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class UsersService extends BackendService {
	public readonly authService: AuthService = inject(AuthService);

	constructor() {
		super('users');
	}

	/**
	 * @deprecated
	 * @param {Date} lastCall
	 * @returns {Observable<IListUsersResponse>}
	 * @private
	 */
	private ___listUsers(lastCall?: Date): Observable<IListUsersResponse> {
		return this.apiService.get(this.getUrl(), {lastCall});
	}

	public queryUsers(request: IQueryUsersRequestParams): Observable<IQueryUsersResponse> {
		return this.apiService.get(this.getUrl('query'), {query: request.query, page: request.page, size: request.size});
	}

	/**
	 * @description Searches for a user by either:
	 * - id
	 * - username
	 * - email
	 * - phone number.
	 * @param search
	 * @returns
	 */
	public getUser(search: IGetUserRequest['search']): Observable<IGetUserResponse_> {
		return this.apiService.get(this.getUrl('user'), {search});
	}

	public createUser(request: ICreateUserRequest_): Observable<ICreateUserResponse_> {
		return this.apiService.post(this.getUrl('user'), request);
	}

	public updateUser(request: IUpdateUserRequest_): Observable<IUpdateUserResponse_> {
		return this.apiService.put(this.getUrl('user'), request);
	}

	public updateSelfProfile(request: IUpdateUserRequest_): Observable<IUpdateUserResponse_> {
		return this.apiService.patch(this.getUrl('user'), request);
	}

	public deleteUser(userid: IDeleteUserRequest['userid']): Observable<IDeleteUserResponse> {
		return this.apiService.delete(this.getUrl('user'), {userid});
	}

	public getPortrait(userid: IGetPortraitRequest_['userid']): Observable<IGetPortraitResponse_> {
		return this.apiService.get(this.getUrl('portrait'), {userid});
	}

	public updatePortrait(request: IUpdatePortraitRequest): Observable<IUpdateUserResponse> {
		return this.apiService.post(this.getUrl('portrait'), request);
	}

	public deletePortrait(userid: IDeleteUserRequest['userid']): Observable<IDeleteUserResponse> {
		return this.apiService.delete(this.getUrl('portrait'), {userid});
	}

	public getStamps(userid: IGetUserStampsRequest['userid']): Observable<IGetUserStampsResponse> {
		return this.apiService.get(this.getUrl('stamps'), {userid});
	}

	public createStamp(request: ICreateStampRequest): Observable<ICreateStampResponse> {
		return this.apiService.post(this.getUrl('stamps'), request);
	}

	public deleteStamp(userid: IDeleteStampRequest['userid'], name: IDeleteStampRequest['name']): Observable<IDeleteUserResponse> {
		return this.apiService.delete(this.getUrl('stamps'), {userid, name});
	}

	public createTempUser(request: string, name = ''): Observable<ICreateTempUserResponse> {
		return this.apiService.post(this.getUrl('temp'), null, undefined, {request, name});
	}

	public resetTempUsersLanguage(): Observable<IResetTempUsersLanguageResponse> {
		return this.apiService.delete(this.getUrl('lang'));
	}

	public getGroups(): Observable<IGetGroupsResponse_> {
		return this.apiService.get(this.getUrl('groups'));
	}

	public getGroup(id: IGetGroupRequestParams_['id']): Observable<IGetGroupResponse_> {
		return this.apiService.get(this.getUrl('group'), {id});
	}

	public createGroup(request: ICreateUserGroupRequest): Observable<ICreateUserGroupResponse> {
		return this.apiService.post(this.getUrl('group'), request);
	}

	public updateGroup(request: IUpdateGroupRequest_): Observable<IUpdateGroupResponse_> {
		return this.apiService.put(this.getUrl('group'), request);
	}

	public deleteGroup(id: IDeleteGroupRequest['id']): Observable<IDeleteUserResponse> {
		return this.apiService.delete(this.getUrl('group'), {id});
	}
}
